import React from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  Accordion,
  AccordionContent,
  AccordionPanel,
  Link,
} from '@americanexpress/dls-react';
import { getIsNotLoggedIn } from '@/selectors/authSelectors';
import { IconCardSwipe, IconP2p } from '@americanexpress/dls-icons';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { useRouteParams } from '@/context/routeParamsContext';
import { cn, getCardArtUrl } from '@/helpers/utils';
import { cardArtMapping } from '@/constants/productMapping';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getMycaLoginFlag } from '@/selectors/experimentSelectors';
import {
  useLoungeName,
  useLoungeAccessListMatchedWithLocaleProviders,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { generateIdentifier } from '@/helpers/analyticsHelpers';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useSelectedProductFromUserWallet } from '@/hooks/memberHooks';
import { useModal } from '@/context/modalContext';
import styles from './LoungeDetails.module.scss';
import LoungeAccessProductDescription from '../common/LoungeAccessProductDescription';
import { modalTypes } from '../common/modals/constants';

const LoungeDetailsSidebar = ({ className }) => {
  const { airportCode, product, loungeId, pathname } = useRouteParams();
  const urlSpecifiedProductFromUsersWallet =
    useSelectedProductFromUserWallet(product);
  const loungeName = useLoungeName(loungeId);
  const identifier = generateIdentifier({
    airportCode,
    product,
    loungeId,
    pathname,
  });
  const {
    id: loungeAccessPolicyId,
    accessLevel,
    guestPolicy,
  } = useLoungeAccessListMatchedWithLocaleProviders(loungeId);
  const {
    lounges: { accessPanel },
    lounges,
    selectCard,
  } = useCommonContent();
  const { setModalName } = useModal();

  const [accessPolicyHeaderText, guestPolicyHeaderText] = (lounges?.agTab || '')
    // Across all locales, aside from the exception locales: zh-cn, zh-hk, zh-tw, and ja-jp the "ag_tab" property from the OneCMS data contains (in various languages) the word "Access", the word "Guest", and either the word "and" or an ampersand (surrounded by space characters) between those words. In those exception locales, there's no space before/after the "and" word/symbol. This Regex covers all those current scenarios
    // eslint-disable-next-line unicorn/better-regex  -- the RegEx ES Lint Rule wants to swap "and" with "adn" then remove the vertical pipe that separates the different strings we want to remove. Functionally it works the same, but it makes it difficult for other devs to understand that the goal is to split where " and " or " & " is found in the string.
    .split(/\s[&|and|e|y|et|und]+\s|及|與/);
  // below consts are for the card image swapping
  const { locale } = useSelector(getLocaleData);
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const isNotLoggedIn = useSelector(getIsNotLoggedIn);
  const { enabled: isMycaLoginEnabled } = useSelector(getMycaLoginFlag);
  const cardSwitchLinkText =
    isMycaLoginEnabled && !isNotLoggedIn
      ? selectCard?.link_loggedIn
      : selectCard?.link;
  const imageUrl = getCardArtUrl({
    baseOneCmsImgUrl,
    product: cardArtMapping[product],
    locale,
    isMedium: true,
  });
  const accessAccordionId = uuidv4();
  const guestAccordionId = uuidv4();

  return (
    <div
      className={cn(
        className,
        'margin-3-b',
        styles.loungeOverviewSidebarContainer
      )}
    >
      <div className={cn(styles.loungeOverviewSidebar, 'tw-overflow-hidden')}>
        <Card
          className={cn('tw-overflow-hidden', styles.accessPanelCardContainer)}
          aria-label="Card Callout Notification"
          style={{
            minWidth: 'unset',
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: 'cover',
          }}
        >
          <div
            className="tw-backdrop-blur-1dot5xl"
            style={{ marginBlockEnd: '0' }}
          />
          <div className="tw-flex tw-justify-center">
            <img
              className="tw-rounded-2xl tw-shadow-high sm_tw-mt-2"
              src={imageUrl}
              alt="Card Callout Notification"
            />
          </div>
        </Card>
        <div className="tw-bg-white">
          <div className="tw-p-6 tw-pb-1 tw-font-sans tw-text-xl">
            {urlSpecifiedProductFromUsersWallet && (
              <div className="tw-mb-3 tw-w-fit tw-rounded tw-bg-gradient-to-r tw-from-blue-deep-700 tw-to-blue-bright-shade-600-s8 tw-px-2 tw-py-1 tw-text-sm tw-font-bold tw-uppercase tw-text-white">
                {selectCard?.cardPill_loggedIn}
              </div>
            )}
            <LoungeAccessProductDescription locale={locale} product={product} />
          </div>
          <div className="tw-pb-4 tw-pl-6">
            <button
              id="select-card-button"
              type="button"
              className="tw-text-sm tw-text-blue-bright-shade-600-s8 hover_tw-underline"
              onClick={() => setModalName(modalTypes.SELECT_CARD_MODAL)}
            >
              {cardSwitchLinkText}
            </button>
          </div>
          <hr
            className="tw-mx-6 tw-border-gray-300"
            aria-hidden="true"
            style={{ width: 'unset' }}
          />
          <Accordion
            className="pad-0-b pad-1-lr"
            isBordered={false}
            style={{ paddingTop: 0 }}
          >
            {accessLevel && (
              <>
                <AccordionPanel
                  icon={<IconCardSwipe size="md" />}
                  heading={
                    <h3 className="heading-3">{accessPolicyHeaderText}</h3>
                  }
                  id={`accordion-${accessAccordionId}`}
                  reactlytics={{
                    identifier,
                    element: 'access accordion',
                    meta: {
                      info: `${loungeName} at ${airportCode}`,
                    },
                  }}
                />
                <AccordionContent
                  style={{ paddingTop: 0 }}
                  aria-labelledby={`accordion-${accessAccordionId}`}
                >
                  <div>
                    <span
                      key={loungeAccessPolicyId}
                      dangerouslySetInnerHTML={{ __html: accessLevel }}
                    />
                  </div>
                </AccordionContent>
              </>
            )}
            {guestPolicy && (
              <>
                <AccordionPanel
                  icon={<IconP2p size="md" />}
                  heading={
                    <h3 className="heading-3">{guestPolicyHeaderText}</h3>
                  }
                  id={`accordion-${guestAccordionId}`}
                  className="tw-p-6 tw-pb-0"
                  reactlytics={{
                    identifier,
                    element: 'guest accordion',
                    meta: {
                      info: `${loungeName} at ${airportCode}`,
                    },
                  }}
                />
                <AccordionContent
                  style={{ paddingTop: 0 }}
                  aria-labelledby={`accordion-${guestAccordionId}`}
                >
                  <div>
                    <span
                      key={loungeAccessPolicyId}
                      dangerouslySetInnerHTML={{ __html: guestPolicy }}
                    />
                  </div>
                </AccordionContent>
              </>
            )}
          </Accordion>
          <hr
            className="tw-mx-6 tw-border-gray-300"
            aria-hidden="true"
            style={{ width: 'unset' }}
          />
          <div className="margin-3-l pad-2-t pad-3-b">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- link to open the additional information modals */}
            <Link
              className="tw-text-link"
              onClick={(e) => {
                e.preventDefault();
                setModalName(modalTypes.TERMS_MODAL);
              }}
            >
              {accessPanel?.link}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

LoungeDetailsSidebar.propTypes = {
  className: PropTypes.string,
};

export default LoungeDetailsSidebar;
